import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { UserAccess } from '../types/user';
import { stableStringify } from '@Utils/searchParams';

type UserAccessResponse = { data: UserAccess; meta: Record<string, any> };
type DefaultError = unknown;

type UserAccessParams<S extends Record<string, any> = Record<string, any>> = {
  /** Optional query string params to forward to service.getUserAccess */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
};

export function useUserAccess<
  TData = UserAccessResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UserAccessParams = {},
  options?: Omit<
    UseQueryOptions<UserAccessResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<UserAccessResponse, TError, TData>({
    queryKey: ['userAccess', stableParams],
    enabled: enabled,
    queryFn: async () => {
      return service.getUserAccess(searchParams ?? {});
    },
    ...(options as any),
  });
}
